import { requestApi } from 'api/api'
import {
  AddBeneficiaryRequest,
  AddBeneficiaryRequestPreRequest,
  BeneficiaryDetails,
  BeneficiaryType,
  UUID,
} from 'types/beneficiaries'
import { ContactInstrument } from 'types/contacts'
import { WithPaginationApiResponse } from 'types/pagination'
import { API_PAGINATION_LIMIT, API_PAGINATION_OFFSET } from 'utils/pagination'

export interface BeneficiaryListRequestParams {
  size?: number
  page?: number
  sort?: string
  beneficiaryType?: BeneficiaryType
  currency?: string
}

export const getBeneficiaryList = ({
  size = API_PAGINATION_LIMIT,
  page = API_PAGINATION_OFFSET,
  sort = '',
  beneficiaryType,
  currency,
}: BeneficiaryListRequestParams): Promise<WithPaginationApiResponse<BeneficiaryDetails[]>> => {
  return requestApi({
    url: '/beneficiaries',
    method: 'GET',
    params: {
      size,
      page,
      ...(sort && { sort }),
      ...(beneficiaryType && { 'beneficiary-type': beneficiaryType }),
      ...(currency && { currency }),
    },
  })
}

export const addBeneficiary = ({
  authorization: { totp },
  data,
}: AddBeneficiaryRequest): Promise<WithPaginationApiResponse<BeneficiaryDetails[]>> => {
  return requestApi({
    url: '/beneficiaries',
    method: 'POST',
    headers: {
      totp,
    } as any,
    data,
  })
}

export const addBeneficiaryPreRequest = (data: AddBeneficiaryRequestPreRequest): Promise<void> => {
  const beneficiaryType =
    data?.beneficiaryType === BeneficiaryType.FIRST_PARTY ? 'first-party' : 'third-party'
  return requestApi({
    url: `/beneficiaries/${beneficiaryType}`,
    method: 'POST',
    data,
  })
}

export const addBeneficiaryPostChallengeRequest = ({
  mfaCallbackUrl,
  challenge,
  tokenResponse,
}: {
  challenge: UUID
  tokenResponse: string
  mfaCallbackUrl: string
}): Promise<BeneficiaryDetails | ContactInstrument> => {
  return requestApi({
    baseUrl: mfaCallbackUrl,
    url: '',
    method: 'POST',
    data: {
      challenge,
      tokenResponse,
    },
  })
}

export const updateBeneficiaryPreRequest = (beneficiary: BeneficiaryDetails): Promise<void> => {
  const { reference, ...payload } = beneficiary
  return requestApi({
    url: `/beneficiaries/${reference}`,
    method: 'PUT',
    data: payload,
  })
}

export const updateBeneficiaryPostChallengeRequest = ({
  challenge,
  tokenResponse,
  mfaCallbackUrl,
}: {
  challenge: UUID
  tokenResponse: string
  mfaCallbackUrl: string
}): Promise<BeneficiaryDetails> => {
  return requestApi({
    baseUrl: mfaCallbackUrl,
    url: '',
    method: 'POST',
    data: {
      challenge,
      tokenResponse,
    },
  })
}

export const deleteBeneficiary = (uuid: UUID): Promise<void> => {
  return requestApi({
    url: `/beneficiaries/${uuid}`,
    method: 'DELETE',
  })
}

export const updateBeneficiaryAsMigrated = async (
  beneficiary: BeneficiaryDetails,
  instrumentId: string
) => {
  return requestApi({
    url: `/beneficiaries/${beneficiary?.reference}/payment-instrument/${instrumentId}`,
    method: 'PUT',
    data: { ...beneficiary, paymentInstrumentId: instrumentId },
  })
}
